import React, { useState, useEffect, useCallback } from 'react';
import environment from '../../config/environment';
import {
  LineChart,
  BarChart,
  PieChart,
  VisitorTable,
  MetricsCard,
  TimeframeSelector,
  EngagementMetrics,
  FunnelChart,
  ResourceAnalytics,
  ApplicationAnalytics,
  AcademyAnalytics,
  DonationAnalytics,
  VisitorAnalytics,
  LoadingSpinner,
  ErrorMessage,
  ExportButton
} from '../../components/Analytics';
import { defaultAnalyticsData } from '../../types/analyticsTypes';
import '../../styles/Analytics.css';
import generateMockData from '../../services/mockAnalyticsService';
import HeatMapAnalytics from '../../components/Analytics/HeatMapAnalytics';
import TrackingService from '../../services/TrackingService';
import VisitorInsights from '../../components/Analytics/VisitorInsights';
import ExternalAnalyticsDashboard from '../../components/Analytics/ExternalAnalytics/ExternalAnalyticsDashboard';
import ServerAnalyticsDashboard from '../../components/Analytics/ServerAnalytics/ServerAnalyticsDashboard';
import '../../styles/ExternalAnalytics.css';

const Analytics = () => {
  const [activeTab, setActiveTab] = useState('external');
  const [timeframe, setTimeframe] = useState('7d');
  const [metrics, setMetrics] = useState(defaultAnalyticsData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { apiUrl } = environment();

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching analytics with timeframe:', timeframe);
      
      const token = localStorage.getItem('token');
      
      let data;
      try {
        const response = await fetch(`${apiUrl}/api/analytics/dashboard?timeframe=${timeframe}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token || ''}`,
            'Accept': 'application/json',
            'Origin': window.location.origin
          },
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`API responded with status: ${response.status}`);
        }
        
        data = await response.json();
        console.log('Analytics data received from API:', data);
        
        // If we don't have heat map data, fetch it separately
        if (!data.heatMapData) {
          try {
            const heatMapResponse = await fetch(`${apiUrl}/api/analytics/heatmap?timeframe=${timeframe}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token || ''}`,
                'Accept': 'application/json',
                'Origin': window.location.origin
              },
              credentials: 'include'
            });
            
            if (heatMapResponse.ok) {
              const heatMapData = await heatMapResponse.json();
              data.heatMapData = heatMapData;
            }
          } catch (heatMapError) {
            console.warn('Failed to fetch heat map data:', heatMapError);
            // Generate mock heat map data if API fails
            data.heatMapData = generateMockHeatMapData();
          }
        }
        
      } catch (apiError) {
        console.warn('API request failed:', apiError);
        // Use mock data instead
        data = generateMockData(timeframe);
        console.log('Using mock data:', data);
      }
      
      // If we have no data, use defaults with a message
      if (!data || Object.keys(data).length === 0) {
        console.log('No analytics data received, using defaults');
        setMetrics({
          ...defaultAnalyticsData,
          message: 'No analytics data available yet'
        });
      } else {
        setMetrics(data);
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [timeframe, apiUrl]);

  useEffect(() => {
    loadAnalytics();
    
    return () => {
      TrackingService.sendHeatMapData('all');
    };
  }, [loadAnalytics]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'visitors', label: 'Visitors', icon: '👥' },
    { id: 'engagement', label: 'Engagement', icon: '🔄' },
    { id: 'heatmaps', label: 'Heat Maps', icon: '🔥' },
    { id: 'resources', label: 'Resources', icon: '📚' },
    { id: 'applications', label: 'Applications', icon: '📝' },
    { id: 'donations', label: 'Donations', icon: '💰' },
    { id: 'academy', label: 'Academy', icon: '🎓' },
    { id: 'insights', label: 'Insights', icon: '💡' },
    { id: 'external', label: 'External Services', icon: '🌐' },
    { id: 'server', label: 'Server & Storage', icon: '🖥️' }
  ];

  if (loading) return <LoadingSpinner message="Loading analytics data..." />;
  
  if (error) {
    return (
      <div className="analytics-error">
        <h2>Analytics Error</h2>
        <p>{error}</p>
        <button 
          className="retry-button"
          onClick={loadAnalytics}
        >
          Retry
        </button>
        <div className="error-details">
          <p>Please check:</p>
          <ul>
            <li>Your internet connection</li>
            <li>API endpoint configuration</li>
            <li>Authentication status</li>
            <li>Server status</li>
          </ul>
          <p>If the problem persists, contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <div className="page-header">
        <h1>Analytics Dashboard</h1>
        <div className="main-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`main-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
      <div className="analytics-content">
        {activeTab === 'overview' && (
          <section className="overview-section">
            <div className="metrics-overview">
              <MetricsCard
                title="Total Visitors"
                value={metrics.totalVisitors || 0}
                change={metrics.visitorChange || 0}
              />
              <MetricsCard
                title="Avg. Session Duration"
                value={metrics.averageSessionDuration || '0m'}
                change={metrics.sessionDurationChange || 0}
              />
              <MetricsCard
                title="Bounce Rate"
                value={metrics.bounceRate || '0%'}
                change={metrics.bounceRateChange || 0}
                inverse
              />
              <MetricsCard
                title="Conversion Rate"
                value={metrics.conversionRate || '0%'}
                change={metrics.conversionRateChange || 0}
              />
            </div>
            <div className="charts-grid">
              {metrics.visitorTrends && metrics.visitorTrends.length > 0 && (
                <div className="chart-container">
                  <h3>Visitor Trends</h3>
                  <LineChart 
                    data={metrics.visitorTrends}
                    xAxis="Date"
                    yAxis="Visitors"
                  />
                </div>
              )}
              {metrics.pageViews && (
                <div className="chart-container">
                  <h3>Top Pages</h3>
                  <BarChart 
                    data={Object.entries(metrics.pageViews || {}).map(([page, stats]) => ({
                      x: page,
                      y: stats.views || 0
                    })).slice(0, 5)}
                    xAxis="Page"
                    yAxis="Views"
                  />
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === 'visitors' && (
          <section className="visitors-section">
            <div className="metrics-grid">
              <MetricsCard 
                title="Total Visitors" 
                value={metrics.totalVisitors || 0} 
                change={metrics.visitorChange || 0} 
                icon="👥"
              />
              <MetricsCard 
                title="New Visitors" 
                value={metrics.newVisitors || 0} 
                change={metrics.newVisitorChange || 0} 
                icon="✨"
              />
              <MetricsCard 
                title="Returning Visitors" 
                value={metrics.returningVisitors || 0} 
                change={metrics.returningVisitorChange || 0} 
                icon="🔄"
              />
              <MetricsCard 
                title="Avg. Session Duration" 
                value={metrics.avgSessionDuration || '0:00'} 
                change={metrics.sessionDurationChange || 0} 
                icon="⏱️"
              />
            </div>
            
            <div className="chart-row">
              <div className="chart-container">
                <h3>Visitor Trends</h3>
                <LineChart 
                  data={metrics.visitorTrends || []} 
                  xKey="date" 
                  yKey="visitors" 
                  color="#10B981"
                />
              </div>
              <div className="chart-container">
                <h3>Traffic Sources</h3>
                <PieChart 
                  data={metrics.trafficSources || []} 
                  nameKey="source" 
                  valueKey="visitors"
                />
              </div>
            </div>
            
            <VisitorInsights visitorData={metrics.visitorData || []} />
            
            <div className="chart-row">
              <div className="chart-container">
                <h3>Devices</h3>
                <BarChart 
                  data={metrics.deviceData || []} 
                  xKey="device" 
                  yKey="visitors"
                  color="#8B5CF6"
                />
              </div>
              <div className="chart-container">
                <h3>Locations</h3>
                <VisitorTable 
                  data={metrics.locationData || []}
                  columns={[
                    { key: 'country', label: 'Country' },
                    { key: 'visitors', label: 'Visitors' },
                    { key: 'percentage', label: '%', format: 'percent' }
                  ]}
                />
              </div>
            </div>
          </section>
        )}

        {activeTab === 'engagement' && (
          <section className="engagement-section">
            <EngagementMetrics data={metrics.engagement || {}} />
          </section>
        )}

        {activeTab === 'resources' && (
          <section className="resources-section">
            <ResourceAnalytics data={metrics.resourceAnalytics || {}} />
          </section>
        )}

        {activeTab === 'applications' && (
          <section className="applications-section">
            <ApplicationAnalytics data={metrics.applicationAnalytics || {}} />
          </section>
        )}

        {activeTab === 'donations' && (
          <section className="donations-section">
            <DonationAnalytics data={metrics.donationAnalytics || {}} />
          </section>
        )}

        {activeTab === 'academy' && (
          <section className="academy-section">
            <AcademyAnalytics data={metrics.academyAnalytics || {}} />
          </section>
        )}

        {activeTab === 'insights' && (
          <section className="insights-section">
            <h3>Key Insights</h3>
            <div className="insights-content">
              <p>No insights available yet. Check back later as more data is collected.</p>
            </div>
          </section>
        )}

        {activeTab === 'heatmaps' && (
          <section className="heatmaps-section">
            <HeatMapAnalytics data={metrics.heatMapData || {}} timeframe={timeframe} />
          </section>
        )}

        {activeTab === 'external' && <ExternalAnalyticsDashboard />}
        {activeTab === 'server' && <ServerAnalyticsDashboard />}
      </div>
    </div>
  );
};

export default Analytics;

const generateMockHeatMapData = () => {
  // Include all types of pages
  const pages = [
    // Public pages
    '/', '/our-story', '/causes', '/donate', '/apply', '/services', '/impact-academy',
    // Portal pages
    '/portal', '/portal/dashboard', '/portal/documents', '/portal/reports', '/portal/profile',
    // Admin pages
    '/admin/dashboard', '/admin/users', '/admin/settings', '/admin/analytics', '/admin/resources',
    // Academy pages
    '/academy', '/academy/courses', '/academy/resources', '/academy/webinars',
    // Application pages
    '/apply', '/apply/form', '/apply/submit', '/apply/status',
    // Donation pages
    '/donate', '/donate/recurring', '/donate/thank-you', '/donate/impact'
  ];
  
  const result = {
    clickHeatMap: {},
    moveHeatMap: {},
    scrollHeatMap: {},
    pageScreenshots: {},
    topPages: pages
  };
  
  // Generate mock data for each page
  pages.forEach(page => {
    // Determine density based on page type
    let density = 50; // Default
    if (page.includes('/admin')) density = 80;
    if (page.includes('/portal')) density = 70;
    if (page.includes('/apply')) density = 90;
    if (page.includes('/donate')) density = 100;
    
    // Click data - more concentrated on interactive elements
    const clickPoints = [];
    for (let i = 0; i < density; i++) {
      // Create clusters of clicks around likely UI elements
      const isButton = Math.random() > 0.7;
      const isNavigation = Math.random() > 0.6 && !isButton;
      
      let x, y, value;
      
      if (isButton) {
        // Buttons tend to be in certain areas
        x = 50 + (Math.random() * 40 - 20); // Center-ish
        y = 70 + (Math.random() * 20); // Lower half
        value = Math.floor(Math.random() * 8) + 3; // Higher values
      } else if (isNavigation) {
        // Navigation tends to be at the top
        x = Math.random() * 100;
        y = 10 + (Math.random() * 10); // Top area
        value = Math.floor(Math.random() * 6) + 2;
      } else {
        // Random content clicks
        x = Math.random() * 100;
        y = Math.random() * 100;
        value = Math.floor(Math.random() * 5) + 1;
      }
      
      clickPoints.push({
        x, y, value,
        label: isButton ? 'Button' : isNavigation ? 'Nav' : Math.random() > 0.5 ? 'Link' : 'Content'
      });
    }
    result.clickHeatMap[page] = clickPoints;
    
    // Movement data - more spread out
    const movePoints = [];
    for (let i = 0; i < density * 4; i++) {
      movePoints.push({
        x: Math.random() * 100,
        y: Math.random() * 100,
        value: Math.floor(Math.random() * 3) + 1
      });
    }
    result.moveHeatMap[page] = movePoints;
    
    // Scroll data - decreasing as you go down
    const scrollPoints = [];
    for (let i = 0; i < 100; i++) {
      // More activity at the top, less as you scroll down
      const value = Math.max(1, Math.floor((100 - i) / 10) + Math.floor(Math.random() * 3));
      scrollPoints.push({
        y: i,
        value
      });
    }
    result.scrollHeatMap[page] = scrollPoints;
  });
  
  return result;
};

const generateMockVisitorData = () => {
  const visitorTypes = ['anonymous', 'lead', 'applicant', 'donor', 'partner', 'admin'];
  const authProviders = ['google', 'facebook', 'linkedin', 'email', null];
  const names = [
    'John Smith', 'Maria Garcia', 'Wei Chen', 'Aisha Johnson', 'Carlos Rodriguez',
    'Emma Wilson', 'Raj Patel', 'Sophia Kim', 'Michael Brown', 'Fatima Ahmed',
    'David Lee', 'Olivia Martinez', 'James Taylor', 'Zoe Williams', 'Omar Hassan'
  ];
  const domains = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'company.org', 'nonprofit.org'];
  
  const visitors = [];
  
  // Generate 50 mock visitors
  for (let i = 0; i < 50; i++) {
    const userType = visitorTypes[Math.floor(Math.random() * visitorTypes.length)];
    const authProvider = authProviders[Math.floor(Math.random() * authProviders.length)];
    const isAuthenticated = authProvider !== null;
    const name = isAuthenticated ? names[Math.floor(Math.random() * names.length)] : null;
    
    // Create visitor
    const visitor = {
      visitorId: `visitor_${i}_${Math.random().toString(36).substring(2, 10)}`,
      userType,
      authenticated: isAuthenticated,
      authProvider,
      pageViews: Math.floor(Math.random() * 20) + 1,
      timeOnSite: Math.floor(Math.random() * 3600), // Up to 1 hour in seconds
      lastUpdated: new Date(Date.now() - Math.floor(Math.random() * 7 * 24 * 60 * 60 * 1000)).toISOString() // Within last 7 days
    };
    
    // Add authenticated user details
    if (isAuthenticated) {
      visitor.name = name;
      visitor.email = `${name.toLowerCase().replace(' ', '.')}@${domains[Math.floor(Math.random() * domains.length)]}`;
      visitor.userId = `user_${Math.random().toString(36).substring(2, 10)}`;
      
      // Add UTM data for some visitors
      if (Math.random() > 0.7) {
        visitor.utmData = {
          source: ['google', 'facebook', 'linkedin', 'twitter', 'email'][Math.floor(Math.random() * 5)],
          medium: ['cpc', 'social', 'email', 'referral', 'organic'][Math.floor(Math.random() * 5)],
          campaign: ['spring_fundraiser', 'volunteer_drive', 'annual_gala', 'newsletter'][Math.floor(Math.random() * 4)]
        };
      }
    }
    
    visitors.push(visitor);
  }
  
  return visitors;
}; 